<template>
  <div class="tw-relative">
    <!-- Popover-based Multi-Select -->
    <div class="tw-relative">
      <Popover v-model:open="isOpen">
        <PopoverTrigger as-child>
          <input
            :aria-expanded="isOpen"
            class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors tw-cursor-pointer tw-text-left"
            :class="selectedValues.length > 0 ? 'tw-text-secondary-500' : 'tw-text-gray-400'"
            :value="selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder"
            :placeholder="placeholder"
          />
        </PopoverTrigger>
        <PopoverContent
          class="tw-w-full tw-p-0 tw-z-[200] !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
          align="start"
          @click.stop
        >
          <div class="tw-p-4 tw-border-b tw-border-gray-200">
            <div class="tw-mb-3">
              <span class="tw-text-sm tw-font-medium tw-text-gray-700 tw-block tw-mb-2">
                {{ placeholder }}
              </span>
              <div class="tw-flex tw-gap-1 tw-justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  @click.stop="selectAllOptions"
                  class="tw-h-7 tw-px-2 tw-text-xs tw-text-blue-600 tw-hover:text-blue-800 tw-hover:bg-blue-50 tw-transition-colors"
                >
                  {{ t('common.actions.select_all') }}
                </Button>
                <Button
                  v-if="selectedValues.length > 0"
                  variant="ghost"
                  size="sm"
                  @click.stop="clearAllOptions"
                  class="tw-h-7 tw-px-2 tw-text-xs tw-text-red-600 tw-hover:text-red-800 tw-hover:bg-red-50 tw-transition-colors"
                >
                  {{ t('common.actions.clear_all') }}
                </Button>
              </div>
            </div>
            <Input
              v-model="searchTerm"
              :placeholder="t('common.data.search')"
              class="tw-w-full"
              @click.stop
            />
          </div>
          <div class="tw-max-h-60 tw-overflow-y-auto">
            <div class="tw-p-2">
              <label
                v-for="option in filteredOptions"
                :key="option.value"
                class="tw-flex tw-items-center tw-space-x-2 tw-px-2 tw-py-2 tw-text-sm hover:tw-bg-gray-100 tw-cursor-pointer tw-rounded-md tw-transition-colors"
                @click.stop="toggleOption(option.value)"
              >
                <FancyCheckbox
                  :checked="selectedValues.includes(option.value)"
                  @click.stop
                  class="tw-rounded tw-pointer-events-none"
                />
                <span class="tw-flex-1">{{ option.label }}</span>
              </label>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Header } from '@tanstack/vue-table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import FancyCheckbox from '@/components/shared/fancyTables/FancyCheckbox.vue';
import { ChevronDown } from 'lucide-vue-next';

const { t } = useI18n();

const props = defineProps<{
  header: Header<any, any>;
}>();

const emit = defineEmits<{
  'update:filter': [value: any];
}>();

// Track dropdown state and search
const isOpen = ref(false);
const searchTerm = ref('');

// Get filter options from column definition
const filterOptions = computed(() => {
  const columnDef = props.header.column.columnDef as any;
  const options = columnDef.filterOptions || [];

  // If options is an object, convert to array format
  if (typeof options === 'object' && !Array.isArray(options)) {
    return Object.entries(options).map(([value, label]) => ({
      value,
      label: String(label),
    }));
  }

  // If options is already an array, ensure it has the right format
  if (Array.isArray(options)) {
    return options.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      if (typeof option === 'object' && option.value !== undefined) {
        return option;
      }
      return { value: String(option), label: String(option) };
    });
  }

  return [];
});

// Filtered options based on search term
const filteredOptions = computed(() => {
  if (!searchTerm.value) return filterOptions.value;

  return filterOptions.value.filter(option =>
    option.label.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

// Placeholder text
const placeholder = computed(() => {
  const columnDef = props.header.column.columnDef as any;
  return (
    columnDef.filterPlaceholder ||
    t('common.titles.filter', {
      columnName: columnDef.header,
    })
  );
});

// Track selected values
const selectedValues = ref<any[]>([]);

// Toggle option selection
const toggleOption = (value: string) => {
  const index = selectedValues.value.indexOf(value);
  if (index > -1) {
    selectedValues.value.splice(index, 1);
  } else {
    selectedValues.value.push(value);
  }

  // Update the filter immediately
  updateFilter();
};

// Select all filtered options
const selectAllOptions = () => {
  const newValues = filteredOptions.value
    .filter(option => !selectedValues.value.includes(option.value))
    .map(option => option.value);

  selectedValues.value.push(...newValues);
  searchTerm.value = '';
  updateFilter();
};

// Clear all selected options
const clearAllOptions = () => {
  selectedValues.value = [];
  searchTerm.value = '';
  updateFilter();
};

// Update the column filter
const updateFilter = () => {
  const filterValue =
    selectedValues.value && selectedValues.value.length > 0 ? selectedValues.value : undefined;

  // Emit the update:filter event instead of calling setFilterValue directly
  emit('update:filter', filterValue);
};

// Watch for external filter value changes (when filter is cleared externally)
watch(
  () => props.header.column.getFilterValue(),
  newValue => {
    // Only update if the external value is different from our current state
    const currentValue = selectedValues.value;
    const externalValue = newValue;

    if (Array.isArray(externalValue)) {
      if (JSON.stringify(currentValue.sort()) !== JSON.stringify(externalValue.sort())) {
        selectedValues.value = [...externalValue];
      }
    } else if (externalValue === undefined || externalValue === null) {
      if (currentValue.length > 0) {
        selectedValues.value = [];
      }
    } else {
      if (currentValue.length !== 1 || currentValue[0] !== externalValue) {
        selectedValues.value = [externalValue];
      }
    }
  },
  { immediate: true }
);
</script>
